/**
 * Who else has this recovery runbook open (backend/src/services/runbook-presence.js).
 *
 * Andrew, 8 Oct 2026: a warning when an engineer opens a runbook someone is already working
 * on. The runbook screen saves the whole task list, so two people editing can save over each
 * other; this tells each of them the other is there. It is a warning, not a lock.
 *
 * `runbook:watch` is sent on EVERY connect: room membership belongs to the connection, and
 * socket.io keeps the same client object across a reconnect, so a watch sent once is silently
 * lost the first time the connection drops.
 */
/* eslint-disable @typescript-eslint/no-explicit-any -- socket.io's own listener signature */
export interface RunbookSocket {
    connected: boolean
    on(event: string, handler: (...args: any[]) => void): unknown
    off(event: string, handler: (...args: any[]) => void): unknown
    emit(event: string, ...args: any[]): unknown
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface RunbookViewer {
    userId: number
    name: string | null
    /** When their earliest tab opened it (ISO). */
    since: string
    /** False when they opened it read-only. */
    editing: boolean
}

interface ViewersEvent { requestId: number; viewers: RunbookViewer[] }

/**
 * Watch one runbook on the presence socket. `onViewers` gets everyone in it (this user
 * included) each time someone arrives or leaves, and an empty list while disconnected - a
 * stale "someone is here" is worse than none. Returns the function that stops watching.
 */
export function bindRunbookViewers(
    socket: RunbookSocket,
    requestId: number | string,
    editing: boolean,
    onViewers: (viewers: RunbookViewer[]) => void,
): () => void {
    // The server answers with the row id; the screen may have asked by RequestID.
    let roomId: number | null = null
    const watch = () => {
        socket.emit('runbook:watch', { requestId, editing }, (ack: { ok?: boolean; requestId?: number } | undefined) => {
            if (ack?.ok && typeof ack.requestId === 'number') roomId = ack.requestId
        })
    }
    const onEvent = (event: ViewersEvent) => {
        if (!event || !Array.isArray(event.viewers)) return
        // A socket watches one runbook at a time (the server leaves the last one), so the only
        // event that is not this runbook's is a late one from the runbook just left.
        const mine = roomId == null || event.requestId === roomId
        if (mine) onViewers(event.viewers)
    }
    const onDisconnect = () => onViewers([])

    socket.on('runbook:viewers', onEvent)
    socket.on('connect', watch)
    socket.on('disconnect', onDisconnect)
    if (socket.connected) watch()

    return () => {
        socket.off('runbook:viewers', onEvent)
        socket.off('connect', watch)
        socket.off('disconnect', onDisconnect)
        if (socket.connected) socket.emit('runbook:unwatch', {})
        onViewers([])
    }
}

/** The other people, split by whether they can change the runbook. */
export function otherViewers(viewers: RunbookViewer[], myUserId: number | null | undefined) {
    const others = (viewers || []).filter((v) => v && v.userId !== myUserId)
    return { working: others.filter((v) => v.editing), reading: others.filter((v) => !v.editing) }
}
